import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .archive-link {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    &:after {
      bottom: 0.1em;
    }
  }

  .timeline-container {
    position: relative;
    width: 100%;
    max-width: 800px;
    margin-top: 50px;

    &::before {
      content: '';
      position: absolute;
      left: 20px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: var(--green);

      @media (max-width: 768px) {
        left: 15px;
      }
    }
  }

  .timeline-item {
    position: relative;
    margin-bottom: 40px;
    padding-left: 60px;

    @media (max-width: 768px) {
      padding-left: 50px;
    }

    &::before {
      content: '';
      position: absolute;
      left: 12px;
      top: 20px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: var(--green);
      border: 3px solid var(--navy);
      z-index: 2;

      @media (max-width: 768px) {
        left: 7px;
        width: 14px;
        height: 14px;
      }
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }
`;

const StyledProject = styled.li`
  position: relative;
  cursor: pointer;
  transition: var(--transition);

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    position: relative;
    padding: 1.5rem;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    border-left: 3px solid var(--green);

    &:hover {
      transform: translateX(5px);
      background-color: var(--navy);
    }
  }

  .project-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 15px;
  }

  .project-title {
    margin: 0;
    color: var(--lightest-slate);
    font-size: var(--fz-lg);
    font-weight: 600;
  }

  .project-date {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    margin-top: 5px;
  }

  .project-tech {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 15px 0;

    span {
      background: var(--navy);
      color: var(--green);
      padding: 4px 8px;
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
    }
  }

  .project-description {
    color: var(--light-slate);
    font-size: var(--fz-sm);
    line-height: 1.6;
    margin-bottom: 15px;
  }

  .project-links {
    display: flex;
    gap: 15px;
    margin-top: 15px;

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 8px 12px;
      background: var(--navy);
      border-radius: 4px;
      color: var(--light-slate);
      font-size: var(--fz-xs);
      transition: var(--transition);

      &:hover {
        background: var(--green);
        color: var(--navy);
      }

      svg {
        width: 16px;
        height: 16px;
        margin-right: 5px;
      }
    }
  }

  .expand-button {
    background: none;
    border: none;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    cursor: pointer;
    padding: 5px 0;
    margin-top: 10px;
    transition: var(--transition);

    &:hover {
      color: var(--lightest-slate);
    }
  }

  .expanded-content {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--navy);

    .detailed-description {
      color: var(--light-slate);
      font-size: var(--fz-sm);
      line-height: 1.7;
      margin-bottom: 20px;
    }

    .project-features {
      margin: 20px 0;

      h4 {
        color: var(--lightest-slate);
        font-size: var(--fz-md);
        margin-bottom: 10px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          color: var(--light-slate);
          font-size: var(--fz-sm);
          margin-bottom: 8px;
          padding-left: 20px;
          position: relative;

          &::before {
            content: '▹';
            position: absolute;
            left: 0;
            color: var(--green);
          }
        }
      }
    }
  }
`;

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
              date
            }
            html
          }
        }
      }
    }
  `);

  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const toggleProject = projectTitle => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectTitle)) {
      newExpanded.delete(projectTitle);
    } else {
      newExpanded.add(projectTitle);
    }
    setExpandedProjects(newExpanded);
  };

  const GRID_LIMIT = 6;
  const projects = data.projects.edges.filter(({ node }) => node);
  const firstSix = projects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? projects : firstSix;

  const projectInner = node => {
    const { frontmatter, html } = node;
    const { github, external, title, tech, date } = frontmatter;
    const isExpanded = expandedProjects.has(title);

    return (
      <div className="project-inner">
        <div className="project-header">
          <div>
            <h3 className="project-title">{title}</h3>
            {date && <div className="project-date">{new Date(date).getFullYear()}</div>}
          </div>
        </div>

        <div className="project-description">
          {html && <div dangerouslySetInnerHTML={{ __html: `${html.split('</p>')[0]  }</p>` }} />}
        </div>

        {tech && tech.length > 0 && (
          <div className="project-tech">
            {tech.slice(0, 4).map((techItem, i) => (
              <span key={i}>{techItem}</span>
            ))}
            {tech.length > 4 && <span>+{tech.length - 4} more</span>}
          </div>
        )}

        <button className="expand-button" onClick={() => toggleProject(title)}>
          {isExpanded ? 'Show Less' : 'Show More'}
        </button>

        {isExpanded && (
          <div className="expanded-content">
            <div className="detailed-description">
              {html && <div dangerouslySetInnerHTML={{ __html: html }} />}
            </div>

            <div className="project-links">
              {github && (
                <a href={github} aria-label="GitHub Link" target="_blank" rel="noreferrer">
                  <Icon name="GitHub" />
                  GitHub
                </a>
              )}
              {external && (
                <a href={external} aria-label="External Link" target="_blank" rel="noreferrer">
                  <Icon name="External" />
                  Live Demo
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <StyledProjectsSection>
      <h2 ref={revealTitle}>Other Noteworthy Projects</h2>

      <Link className="inline-link archive-link" to="/archive" ref={revealArchiveLink}>
        view the archive
      </Link>

      <div className="timeline-container">
        {prefersReducedMotion ? (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <div key={i} className="timeline-item">
                  <StyledProject ref={el => (revealProjects.current[i] = el)}>
                    {projectInner(node)}
                  </StyledProject>
                </div>
              ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <CSSTransition
                  key={i}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                  exit={false}>
                  <div className="timeline-item">
                    <StyledProject
                      ref={el => (revealProjects.current[i] = el)}
                      style={{
                        transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                      }}>
                      {projectInner(node)}
                    </StyledProject>
                  </div>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </div>

      {projects.length > GRID_LIMIT && (
        <button className="more-button" onClick={() => setShowMore(!showMore)}>
          Show {showMore ? 'Less' : 'More'}
        </button>
      )}
    </StyledProjectsSection>
  );
};

export default Projects;
